import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { ProfilecardTriggerPosition } from '@atlaskit/profilecard/types';
import { TeamsAppAnalyticsContext } from '@atlaskit/teams-app-internal-analytics';

import { ReportingLinesProvider } from './ReportingLinesContext';
import {
	DEPRECATED_ReportingLinesWithQuery,
	ReportingLinesWithQuery as ReportingLinesWithQueryNext,
} from './ReportingLinesWithQuery';

type ReportingLinesProps = {
	product?: string;
	userId: string;
	WrappedWithProfilecard?: React.ComponentType<{
		children: React.ReactElement;
		userId: string;
		position?: ProfilecardTriggerPosition;
	}>;
};

/**
 * The Reporting Lines component is a resourced component that fetches the reporting lines for a given user and renders them in a chart.
 * @param product - The host product of the reporting lines, use for analytics.
 * @param userId - The ID of the user to get the reporting lines for the starting point of the chart.
 * @param WrappedWithProfilecard - A resourced component to wrap reporting lines in profile cards with actions relevant to the context.
 */
const ReportingLines = ({
	product,
	userId,
	WrappedWithProfilecard,
}: ReportingLinesProps): React.JSX.Element => {
	const ReportingLinesWithQuery = fg('ptc-consume-reporting-lines')
		? ReportingLinesWithQueryNext
		: DEPRECATED_ReportingLinesWithQuery;

	/**
	 * This is the same code block as below but with analytics context.
	 */
	if (fg('ptc-enable-ptc-common-analytics-refactor')) {
		return (
			<TeamsAppAnalyticsContext data={{ source: 'userProfileScreen' }}>
				<ReportingLinesProvider product={product} WrappedWithProfilecard={WrappedWithProfilecard}>
					<ReportingLinesWithQuery userId={userId} />
				</ReportingLinesProvider>
			</TeamsAppAnalyticsContext>
		);
	}

	return (
		<ReportingLinesProvider product={product} WrappedWithProfilecard={WrappedWithProfilecard}>
			<ReportingLinesWithQuery userId={userId} />
		</ReportingLinesProvider>
	);
};

export default ReportingLines;
